/**
 * Iter136.20 — Markdown body renderer + super-admin inline editor for
 * the user manual.
 *
 *  • Reader mode (everyone): renders ``sec.summary`` as Markdown using
 *    ``react-markdown`` with ``remark-gfm``. Inline images (uploaded
 *    by super admins) are styled, captions auto-derived from the alt
 *    text. The summary stays inline so screenshots interleave with
 *    paragraphs naturally — that's the "between paragraphs" UX the
 *    user asked for.
 *
 *  • Editor mode (super admin only): triggered by the pencil icon on
 *    the section card. Renders a textarea with the raw Markdown plus
 *    a thin formatting toolbar:
 *       Bold · Italic · H2 · H3 · • List · 1. List · Insert image
 *    "Insert image" pops a file picker, uploads to
 *    ``POST /api/manual/screenshots`` (super-admin gated), then
 *    inserts ``![caption](url)`` at the cursor.
 *
 *    A live Markdown preview renders side-by-side. Save calls
 *    ``PUT /api/manual/section-overrides`` with the role × section ×
 *    lang triple. "Revert to default" sends an empty summary string
 *    which deletes the override row server-side and falls back to the
 *    hardcoded text from ``manual_content.py``.
 *
 *  Non-goals (deferred):
 *    • WYSIWYG (Tiptap / Lexical). Markdown source + live preview
 *      keeps the bundle slim and is more than enough for help copy.
 *    • Inline image drag-reorder. Re-ordering happens by editing the
 *      Markdown — fine for static help docs.
 */
import { useState, useRef, useCallback, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import {
  PencilSimple, FloppyDisk, X, ArrowCounterClockwise, ImageSquare,
  TextB, TextItalic, TextH, TextHFour, ListBullets, ListNumbers, CircleNotch,
} from "@phosphor-icons/react";
import api from "@/lib/api";

const MD_COMPONENTS = {
  // Style headings inside section bodies a bit smaller than the
  // section title so they feel like sub-headers.
  h1: ({ node, ...p }) => <h3 className="text-base font-semibold text-slate-900 mt-3 mb-1" {...p} />,
  h2: ({ node, ...p }) => <h4 className="text-sm font-semibold text-slate-900 mt-3 mb-1" {...p} />,
  h3: ({ node, ...p }) => <h5 className="text-sm font-medium text-slate-800 mt-2 mb-1" {...p} />,
  p:  ({ node, ...p }) => <p className="text-sm text-slate-700 leading-relaxed my-2" {...p} />,
  ul: ({ node, ...p }) => <ul className="text-sm text-slate-700 list-disc pl-5 my-2 space-y-1" {...p} />,
  ol: ({ node, ...p }) => <ol className="text-sm text-slate-700 list-decimal pl-5 my-2 space-y-1" {...p} />,
  a:  ({ node, ...p }) => <a className="text-blue-700 hover:underline" target="_blank" rel="noopener noreferrer" {...p} />,
  // react-markdown v10 dropped the `inline` prop — the `code`
  // component is now called for BOTH inline (`code`) and fenced
  // block (```code```) markdown. Block-level code carries a
  // ``language-xxx`` className from the parser; inline code does
  // not. We use that to render a tight chip inline vs a full
  // <pre> block.
  code: ({ node, className, children, ...p }) => {
    const isBlock = /(?:^|\s)language-/.test(className || "");
    if (isBlock) {
      return (
        <pre className="bg-slate-50 border border-slate-200 rounded p-2 my-2 text-[12px] font-mono overflow-x-auto">
          <code className={className} {...p}>{children}</code>
        </pre>
      );
    }
    return (
      <code
        className="bg-slate-100 text-slate-800 rounded px-1 py-0.5 text-[12px] font-mono"
        {...p}
      >
        {children}
      </code>
    );
  },
  img: ({ node, src, alt, ...p }) => (
    <figure className="my-3" data-testid={`manual-md-image-${(src || "").split("/").pop()}`}>
      <img
        src={src}
        alt={alt || ""}
        className="max-w-full rounded-md border border-slate-200 shadow-sm"
        loading="lazy"
        {...p}
      />
      {alt ? <figcaption className="text-xs text-slate-500 mt-1 italic">{alt}</figcaption> : null}
    </figure>
  ),
};


export function ManualMarkdown({ source, testId }) {
  if (!source) return null;
  return (
    <div data-testid={testId} className="manual-md">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
        {source}
      </ReactMarkdown>
    </div>
  );
}


/**
 * Editor wrapping a controlled textarea + toolbar. Returned via a
 * render-prop pattern so the parent can mount it conditionally.
 */
export function ManualSectionEditor({
  role, sectionId, lang, initialSummary, isOverridden, onSaved, onCancel, t,
}) {
  const [draft, setDraft] = useState(initialSummary || "");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const taRef = useRef(null);
  const fileRef = useRef(null);
  const dirty = (draft || "") !== (initialSummary || "");

  const insertAtCursor = useCallback((before, after = "") => {
    const ta = taRef.current;
    if (!ta) {
      setDraft((d) => `${d}${before}${after}`);
      return;
    }
    const start = ta.selectionStart ?? draft.length;
    const end = ta.selectionEnd ?? draft.length;
    const selected = draft.slice(start, end);
    const next = `${draft.slice(0, start)}${before}${selected}${after}${draft.slice(end)}`;
    setDraft(next);
    requestAnimationFrame(() => {
      try {
        ta.focus();
        const newCursor = start + before.length + selected.length;
        ta.setSelectionRange(newCursor, newCursor);
      } catch { /* ignore */ }
    });
  }, [draft]);

  const onPickImage = useCallback(async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error(t("manual_editor.image_type_error", "Use JPG / PNG / WEBP."));
      e.target.value = "";
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error(t("manual_editor.image_size_error", "Image exceeds 5 MB."));
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("role", role);
      fd.append("section_id", sectionId);
      fd.append("lang", lang);
      fd.append("caption", "");
      fd.append("upload", f);
      const r = await api.post("/manual/screenshots", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r.data?.url;
      if (!url) throw new Error("upload returned no url");
      // Insert markdown at cursor; alt text becomes the caption.
      insertAtCursor(`\n\n![${f.name.replace(/\.[^.]+$/, "")}](${url})\n\n`);
      toast.success(t("manual_editor.image_uploaded", "Screenshot uploaded."));
    } catch (err) {
      toast.error(t("manual_editor.image_upload_failed", "Upload failed."));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }, [role, sectionId, lang, insertAtCursor, t]);

  const onSave = useCallback(async () => {
    setBusy(true);
    try {
      await api.put("/manual/section-overrides", {
        role, section_id: sectionId, lang, summary: draft,
      });
      toast.success(t("manual_editor.saved", "Saved."));
      onSaved?.(draft);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`${t("manual_editor.save_failed", "Save failed")}: ${detail}`);
    } finally {
      setBusy(false);
    }
  }, [role, sectionId, lang, draft, onSaved, t]);

  const onRevert = useCallback(async () => {
    if (!window.confirm(t("manual_editor.revert_confirm", "Revert this section to the built-in default? Your override will be deleted."))) return;
    setBusy(true);
    try {
      await api.put("/manual/section-overrides", {
        role, section_id: sectionId, lang, summary: "",
      });
      toast.success(t("manual_editor.reverted", "Reverted to default."));
      onSaved?.(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`${t("manual_editor.revert_failed", "Revert failed")}: ${detail}`);
    } finally {
      setBusy(false);
    }
  }, [role, sectionId, lang, onSaved, t]);

  const ToolbarBtn = useMemo(() => function ToolbarBtn({ onClick, title, testid, children, disabled }) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        title={title}
        disabled={disabled}
        data-testid={testid}
        className="inline-flex items-center justify-center w-7 h-7 rounded border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-50 text-slate-700"
      >
        {children}
      </button>
    );
  }, []);

  return (
    <div
      data-testid={`manual-editor-${sectionId}`}
      className="border border-amber-300 rounded-md bg-amber-50/40 p-3"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        <ToolbarBtn testid={`mb-${sectionId}-bold`}   title="Bold"           onClick={() => insertAtCursor("**", "**")}><TextB size={14} weight="bold" /></ToolbarBtn>
        <ToolbarBtn testid={`mb-${sectionId}-italic`} title="Italic"         onClick={() => insertAtCursor("*", "*")}><TextItalic size={14} weight="bold" /></ToolbarBtn>
        <ToolbarBtn testid={`mb-${sectionId}-h2`}     title="Heading"        onClick={() => insertAtCursor("\n\n## ", "\n\n")}><TextH size={14} weight="bold" /></ToolbarBtn>
        <ToolbarBtn testid={`mb-${sectionId}-h3`}     title="Sub-heading"    onClick={() => insertAtCursor("\n\n### ", "\n\n")}><TextHFour size={14} weight="bold" /></ToolbarBtn>
        <ToolbarBtn testid={`mb-${sectionId}-ul`}     title="Bulleted list"  onClick={() => insertAtCursor("\n- ", "")}><ListBullets size={14} weight="bold" /></ToolbarBtn>
        <ToolbarBtn testid={`mb-${sectionId}-ol`}     title="Numbered list"  onClick={() => insertAtCursor("\n1. ", "")}><ListNumbers size={14} weight="bold" /></ToolbarBtn>
        <span className="w-px h-5 bg-slate-300 mx-1" />
        <ToolbarBtn
          testid={`mb-${sectionId}-img`}
          title={t("manual_editor.insert_image_hint", "Upload + insert a screenshot at the cursor")}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? <CircleNotch size={14} className="animate-spin" /> : <ImageSquare size={14} weight="bold" />}
        </ToolbarBtn>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
               className="hidden" onChange={onPickImage}
               data-testid={`manual-editor-${sectionId}-file-input`} />
        <span className="text-[10px] uppercase tracking-wider text-amber-700 ml-2">
          {t("manual_editor.markdown_label", "Markdown")} · {lang.toUpperCase()}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <textarea
          ref={taRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t("manual_editor.placeholder", "Write Markdown here. Use the toolbar to insert images.")}
          className="w-full min-h-[260px] text-[13px] font-mono leading-relaxed p-3 bg-white border border-slate-300 rounded resize-y focus:outline-none focus:ring-1 focus:ring-amber-400"
          data-testid={`manual-editor-${sectionId}-textarea`}
        />
        <div
          className="min-h-[260px] p-3 bg-white border border-slate-200 rounded overflow-auto"
          data-testid={`manual-editor-${sectionId}-preview`}
        >
          {draft ? (
            <ManualMarkdown source={draft} testId={`manual-editor-${sectionId}-preview-md`} />
          ) : (
            <p className="text-xs text-slate-400 italic">
              {t("manual_editor.preview_empty", "Live preview appears here.")}
            </p>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        {isOverridden && (
          <button
            type="button"
            onClick={onRevert}
            disabled={busy}
            data-testid={`manual-editor-${sectionId}-revert`}
            className="text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-7 rounded-sm border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50"
          >
            <ArrowCounterClockwise size={12} weight="bold" />
            {t("manual_editor.revert", "Revert to default")}
          </button>
        )}
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          data-testid={`manual-editor-${sectionId}-cancel`}
          className="text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-7 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <X size={12} weight="bold" />
          {t("manual_editor.cancel", "Cancel")}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={busy || !dirty}
          data-testid={`manual-editor-${sectionId}-save`}
          className="text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-7 rounded-sm border border-emerald-500 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {busy ? <CircleNotch size={12} className="animate-spin" /> : <FloppyDisk size={12} weight="bold" />}
          {t("manual_editor.save", "Save")}
        </button>
      </div>
    </div>
  );
}


export function ManualEditButton({ onClick, isOverridden, t }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      data-testid="manual-section-edit-btn"
      className={
        "text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1 px-2 h-7 rounded-sm border " +
        (isOverridden
          ? "border-amber-400 bg-amber-100 text-amber-800 hover:bg-amber-200"
          : "border-slate-300 text-slate-700 hover:bg-slate-50")
      }
      title={t("manual_editor.edit_hint", "Edit summary + screenshots (super admin only)")}
    >
      <PencilSimple size={11} weight="bold" />
      {isOverridden
        ? t("manual_editor.edit_btn_overridden", "Edit override")
        : t("manual_editor.edit_btn", "Edit section")}
    </button>
  );
}
